import { taskEditorApi, type TaskMetaUpdate } from "api/taskEditorApi";
import type {
  TaskEditorBlock,
  TaskEditorState,
} from "pages/task-editor/utils/taskEditorUtils";
import { useCallback, useRef } from "react";
import { useRecoilState } from "recoil";
import {
  taskEditorTasksByTaskIdAtom,
  taskEditorStoreHydratedAtom,
  taskEditorStoreLoadingAtom,
} from "recoil/taskEditorStoreAtom";

export default function useTaskEditorStore() {
  const [tasksByTaskId, setTasksByTaskId] = useRecoilState(
    taskEditorTasksByTaskIdAtom
  );
  const [isStoreHydrated, setIsStoreHydrated] = useRecoilState(
    taskEditorStoreHydratedAtom
  );
  const [isStoreLoading, setIsStoreLoading] = useRecoilState(
    taskEditorStoreLoadingAtom
  );
  const latestTasksByTaskIdRef = useRef<Record<string, TaskEditorState>>({});

  const hydrateTaskEditorStore = useCallback(async () => {
    setIsStoreLoading(true);
    try {
      const nextTasksByTaskId = await taskEditorApi.getTasksByTaskId();
      setTasksByTaskId(nextTasksByTaskId);
      latestTasksByTaskIdRef.current = nextTasksByTaskId;
      setIsStoreHydrated(true);
      return { tasksByTaskId: nextTasksByTaskId };
    } finally {
      setIsStoreLoading(false);
    }
  }, [setIsStoreHydrated, setIsStoreLoading, setTasksByTaskId]);

  // task/detail 로 받은 본문(blocks)을 store 의 해당 활동지에 주입.
  // list 는 메타만 싣고 blocks=[] 라, 선택 시 이 액션으로 본문을 채운다.
  // v1Task(=tasksByTaskId[id]) 가 codeview/turnView/working hydrate 의 base 가 되므로
  // 여기 채워야 그 의존부들이 올바른 V1 본문을 본다.
  const applyTaskBlocks = useCallback(
    (task_id: string, blocks: TaskEditorBlock[]) => {
      if (!task_id) return;
      setTasksByTaskId((currentTasksByTaskId) => {
        const existing = currentTasksByTaskId[task_id];
        if (!existing) return currentTasksByTaskId;
        const nextTasksByTaskId = {
          ...currentTasksByTaskId,
          [task_id]: { ...existing, blocks },
        };
        latestTasksByTaskIdRef.current = nextTasksByTaskId;
        return nextTasksByTaskId;
      });
    },
    [setTasksByTaskId]
  );

  // 메타 단건 저장 (본문 불변). task-meta/save. 본문을 다시 보내지 않는다.
  // upsert — 기존 활동지면 메타만 갱신(blocks 보존), 신규면 추가(blocks=[]).
  // 신규 생성도 이 경로 (생성 = meta 만 save). 낙관적 갱신 후 실패 시 롤백.
  const saveTaskMeta = useCallback(
    async (update: TaskMetaUpdate) => {
      if (!update.task_id) return;

      const previousTasksByTaskId = latestTasksByTaskIdRef.current;

      setTasksByTaskId((currentTasksByTaskId) => {
        const existing = currentTasksByTaskId[update.task_id];
        const nextTasksByTaskId = {
          ...currentTasksByTaskId,
          [update.task_id]: {
            task_id: update.task_id,
            title: update.title,
            set_number: update.set_number,
            is_key_activity: update.is_key_activity,
            sort_order: update.sort_order,
            // 내부 모델은 non-default 만 보관 — basic 은 부재로.
            task_type:
              update.task_type === "basic" ? undefined : update.task_type,
            prerequisite_task_id:
              update.task_type === "tool"
                ? update.prerequisite_task_id
                : undefined,
            linked_tool_task_id:
              update.task_type === "empty"
                ? update.linked_tool_task_id
                : undefined,
            repeat_interval_weeks:
              update.task_type === "repeat"
                ? update.repeat_interval_weeks
                : undefined,
            blocks: existing?.blocks ?? [],
          },
        };
        latestTasksByTaskIdRef.current = nextTasksByTaskId;
        return nextTasksByTaskId;
      });

      try {
        await taskEditorApi.saveTaskMeta(update);
      } catch (error) {
        setTasksByTaskId(() => {
          latestTasksByTaskIdRef.current = previousTasksByTaskId;
          return previousTasksByTaskId;
        });
        throw error;
      }
    },
    [setTasksByTaskId]
  );

  // 메타(set_number/sort_order/title/is_key_activity)만 배치 갱신. 본문 불변.
  // 변경된 활동지만 task-meta/save-all 로 전송. 낙관적으로 store 를 먼저 갱신하고,
  // 서버 실패 시 직전 상태로 롤백한다.
  const reorderTasks = useCallback(
    async (updates: TaskMetaUpdate[]) => {
      if (!Array.isArray(updates) || updates.length === 0) return;

      const previousTasksByTaskId = latestTasksByTaskIdRef.current;

      setTasksByTaskId((currentTasksByTaskId) => {
        const nextTasksByTaskId = { ...currentTasksByTaskId };
        for (const update of updates) {
          const existing = nextTasksByTaskId[update.task_id];
          if (!existing) continue;
          nextTasksByTaskId[update.task_id] = {
            ...existing,
            title: update.title,
            set_number: update.set_number,
            is_key_activity: update.is_key_activity,
            sort_order: update.sort_order,
          };
        }
        latestTasksByTaskIdRef.current = nextTasksByTaskId;
        return nextTasksByTaskId;
      });

      try {
        await taskEditorApi.saveAllTaskMeta(updates);
      } catch (error) {
        setTasksByTaskId(() => {
          latestTasksByTaskIdRef.current = previousTasksByTaskId;
          return previousTasksByTaskId;
        });
        throw error;
      }
    },
    [setTasksByTaskId]
  );

  const removeTask = useCallback(
    async (task_id: string) => {
      if (!task_id) return;

      setTasksByTaskId((currentTasksByTaskId) => {
        if (!(task_id in currentTasksByTaskId)) {
          return currentTasksByTaskId;
        }

        const nextTasksByTaskId = { ...currentTasksByTaskId };
        delete nextTasksByTaskId[task_id];
        latestTasksByTaskIdRef.current = nextTasksByTaskId;
        return nextTasksByTaskId;
      });

      await taskEditorApi.removeTask(task_id);
    },
    [setTasksByTaskId]
  );

  return {
    tasksByTaskId,
    isStoreHydrated,
    isStoreLoading,
    hydrateTaskEditorStore,
    applyTaskBlocks,
    saveTaskMeta,
    reorderTasks,
    removeTask,
  };
}
